import { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux"; // Add this
import { motion, AnimatePresence } from "framer-motion";
import {
  Box,
  IconButton,
  CircularProgress,
  Typography,
  useTheme,
} from "@mui/material";
import {
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
} from "@mui/icons-material";
import { toggleDarkmode } from "store/features/theme/themeSlice"; // Import Redux action
import Lottie from "lottie-react";
import officeAnimation from "assets/login/office.json";
import routes from "routes.js";

export default function Auth() {
  const theme = useTheme();
  const dispatch = useDispatch();

  // Use Redux state instead of useDarkMode hook
  const darkmode = useSelector((state) => state.theme?.darkmode ?? false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleToggleTheme = () => {
    dispatch(toggleDarkmode());
  };

  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.layout === "/auth") {
        return (
          <Route path={`/${prop.path}`} element={prop.component} key={key} />
        );
      }
      return null;
    });
  };

  if (!mounted) {
    return (
      <Box
        sx={{
          display: "flex",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <Box sx={{ textAlign: "center" }}>
          <CircularProgress
            size={48}
            sx={{
              mb: 2,
              color: "primary.main",
              "& .MuiCircularProgress-circle": {
                strokeLinecap: "round",
              },
            }}
          />
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
            }}
          >
            Loading...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      {/* Theme Toggle Button */}
      <IconButton
        onClick={handleToggleTheme}
        sx={{
          position: "fixed",
          right: 24,
          top: 24,
          zIndex: 50,
          width: 44,
          height: 44,
          bgcolor: "background.paper",
          boxShadow: theme.shadows[4],
          backdropFilter: "blur(24px)",
          transition: "all 0.2s",
          "&:hover": {
            transform: "scale(1.05)",
            backgroundColor:
              theme.palette.mode === "dark" ? "#334155" : "#f9fafb",
          },
        }}
      >
        {darkmode ? (
          <LightModeIcon sx={{ color: "primary.main" }} />
        ) : (
          <DarkModeIcon sx={{ color: "#374151" }} />
        )}
      </IconButton>

      {/* Main Container */}
      <Box
        sx={{
          position: "relative",
          mx: "auto",
          minHeight: "100vh",
          maxWidth: "1280px",
          px: { xs: 2, sm: 3, lg: 4 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            minHeight: "100vh",
          }}
        >
          {/* LEFT SIDE - Login Form */}
          <Box
            sx={{
              display: "flex",
              flex: 1,
              alignItems: "center",
              justifyContent: "center",
              py: { xs: 6, lg: 0 },
            }}
          >
            <Routes>
              {getRoutes(routes)}
              <Route
                path="/"
                element={<Navigate to="/auth/sign-in" replace />}
              />
            </Routes>
          </Box>

          {/* RIGHT SIDE - Visual Content (Desktop Only) */}
          <Box
            sx={{
              display: { xs: "none", lg: "flex" },
              flex: 1,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
              style={{ position: "relative", width: "100%", maxWidth: "512px" }}
            >
              {/* Decorative Blobs */}
              <Box
                sx={{
                  position: "absolute",
                  left: -80,
                  top: -80,
                  width: 288,
                  height: 288,
                  borderRadius: "50%",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(245,158,11,0.1)"
                      : "rgba(253,230,138,0.3)",
                  filter: "blur(64px)",
                }}
              />
              <Box
                sx={{
                  position: "absolute",
                  right: -80,
                  bottom: -80,
                  width: 384,
                  height: 384,
                  borderRadius: "50%",
                  backgroundColor:
                    theme.palette.mode === "dark"
                      ? "rgba(234,179,8,0.1)"
                      : "rgba(254,240,138,0.3)",
                  filter: "blur(64px)",
                }}
              />

              {/* Lottie Animation */}
              <Box sx={{ position: "relative", mb: 4 }}>
                <Lottie
                  animationData={officeAnimation}
                  loop={true}
                  autoplay={true}
                  style={{ width: "100%", height: "auto" }}
                />
              </Box>

              {/* Quote/Text */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                style={{ textAlign: "center" }}
              >
                <Typography
                  variant="h4"
                  component="h2"
                  sx={{
                    fontWeight: "bold",
                    color: "text.primary",
                  }}
                >
                  Office Management System
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    mt: 1,
                    color: "text.secondary",
                  }}
                >
                  Made Simple & Efficient
                </Typography>
              </motion.div>
            </motion.div>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
